import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';

const SuperAdminLoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const handleLogin = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/super-admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid credentials');
      }

      const data = await response.json();

      // Store session with proper token - SessionManager expects 'access_token'
      SessionManager.setSession('super_admin', {
        access_token: data.access_token,  // This is the key field SessionManager looks for
        role: 'super_admin',
        profile: {
          id: data.profile.id,
          username: data.profile.username,
          email: data.profile.email,
          full_name: data.profile.full_name,
          role: data.profile.role
        }
      });

      setFeedback({
        type: 'success',
        message: 'Welcome to Super Admin Dashboard!',
      });

      setTimeout(() => {
        navigate('/super-admin/dashboard');
      }, 800);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Unable to log in. Please check your credentials.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ==================== LEFT PANEL - Dark Theme with Admin Graphics ==================== */}
      <div className="hidden lg:flex lg:w-[48%] relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-red-500/20 via-orange-500/15 to-transparent blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-purple-500/20 via-pink-500/15 to-transparent blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full bg-red-400/10 blur-2xl"></div>
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNCkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-60 pointer-events-none"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
          {/* Top - Logo */}
          <div>
            <button onClick={() => navigate('/')} className="flex items-center space-x-2 group">
              <div className="w-10 h-10 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-colors">
                <Icon name="admin_panel_settings" size={22} className="text-red-400" />
              </div>
              <span className="text-xl font-bold text-white">Aptirix Admin</span>
            </button>
          </div>

          {/* Center - Hero content */}
          <div className="flex-1 flex flex-col justify-center space-y-8 py-12">
            <div>
              <div className="inline-block px-3 py-1.5 bg-red-400/15 rounded-full text-xs font-semibold text-red-300 border border-red-400/25 mb-6 tracking-wide">
                SUPER ADMIN ACCESS
              </div>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                Platform<br />Control <span className="text-red-400">Center</span>
              </h1>
              <p className="text-gray-400 text-base max-w-sm leading-relaxed">
                Secure access to manage users, monitor system performance, and control all platform operations.
              </p>
            </div>

            {/* Admin features */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <Icon name="dashboard" size={20} className="text-red-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Complete Dashboard</p>
                  <p className="text-gray-400 text-xs">Monitor all platform activities</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Icon name="people" size={20} className="text-orange-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">User Management</p>
                  <p className="text-gray-400 text-xs">Control access and permissions</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Icon name="analytics" size={20} className="text-purple-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Analytics & Reports</p>
                  <p className="text-gray-400 text-xs">Deep insights and metrics</p>
                </div>
              </div>
            </div>

            {/* Security badge */}
            <div className="flex items-center space-x-2 bg-amber-500/10 backdrop-blur-sm rounded-lg p-3 border border-amber-500/20">
              <Icon name="security" size={18} className="text-amber-400" />
              <span className="text-amber-300 text-xs font-medium">All activities are logged and monitored</span>
            </div>
          </div>

          {/* Bottom */}
          <p className="text-gray-500 text-xs">© 2026 Aptirix. Secure Admin Portal.</p>
        </div>
      </div>

      {/* ==================== RIGHT PANEL - White Professional Form ==================== */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 sm:px-12 py-12">
        <div className="w-full max-w-[420px]">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-[#0f172a] rounded-xl flex items-center justify-center">
                <Icon name="admin_panel_settings" size={20} className="text-red-400" />
              </div>
              <span className="text-xl font-bold text-gray-900">Aptirix Admin</span>
            </button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Super Admin Login</h2>
            <p className="text-gray-500 text-sm">Enter your credentials to access the admin panel</p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon name="person" size={18} />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none transition-all text-sm"
                  placeholder="Enter admin username"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon name="lock" size={18} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-10 pr-11 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none transition-all text-sm"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={18} />
                </button>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="security" size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  This is a secure admin area. All login attempts and activities are logged for security purposes.
                </p>
              </div>
            </div>

            {/* Feedback */}
            {feedback.message && (
              <div className={`text-sm px-4 py-3 rounded-lg flex items-start space-x-2 ${
                feedback.type === 'error'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                <Icon name={feedback.type === 'error' ? 'error' : 'check_circle'} size={18} className="mt-0.5 flex-shrink-0" />
                <span>{feedback.message}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!username || !password || isSubmitting}
              className="w-full h-11 bg-gradient-to-r from-red-600 to-red-500 text-white text-sm font-semibold rounded-lg hover:from-red-700 hover:to-red-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center space-x-2 shadow-lg shadow-red-500/30"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <Icon name="admin_panel_settings" size={18} />
                  <span>Secure Login</span>
                </>
              )}
            </button>
          </form>

          {/* Back link */}
          <div className="mt-8 text-center">
            <button 
              onClick={() => navigate('/')} 
              className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors inline-flex items-center space-x-1"
            >
              <Icon name="arrow_back" size={16} />
              <span>Back to Main Site</span>
            </button>
          </div>

          {/* Security Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Protected by enterprise-grade security
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SuperAdminLoginPage;
